import React, { useState, useEffect } from 'react';
import { 
  CalendarOff, 
  Plus, 
  MoreVertical,
  CheckCircle,
  XCircle,
  Clock,
  Umbrella,
  Heart,
  Baby,
  Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { format, differenceInDays } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  cancelled: 'bg-slate-50 text-slate-700 border-slate-200'
};

const leaveTypes = {
  annual: { label: 'Annual Leave', icon: <Umbrella className="h-4 w-4" />, color: 'text-blue-600' },
  sick: { label: 'Sick Leave', icon: <Heart className="h-4 w-4" />, color: 'text-red-600' },
  personal: { label: 'Personal', icon: <Briefcase className="h-4 w-4" />, color: 'text-purple-600' },
  maternity: { label: 'Maternity', icon: <Baby className="h-4 w-4" />, color: 'text-pink-600' },
  paternity: { label: 'Paternity', icon: <Baby className="h-4 w-4" />, color: 'text-cyan-600' },
  unpaid: { label: 'Unpaid', icon: <CalendarOff className="h-4 w-4" />, color: 'text-gray-600' }
};

const LeaveManagement = () => {
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchRequests();
    fetchEmployees();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/hr/leave`);
      setRequests(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/hr/employees`);
      setEmployees(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API_URL}/api/hr/leave`, formData);
      toast.success('Leave request submitted');
      setShowDialog(false);
      setFormData({});
      fetchRequests();
    } catch (err) {
      toast.error('Failed to submit request');
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.put(`${API_URL}/api/hr/leave/${id}/approve`);
      toast.success('Leave approved');
      fetchRequests();
    } catch (err) {
      toast.error('Failed to approve');
    }
  };

  const handleReject = async (id) => {
    try {
      await axios.put(`${API_URL}/api/hr/leave/${id}/reject`);
      toast.success('Leave rejected');
      fetchRequests();
    } catch (err) {
      toast.error('Failed to reject');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this request?')) {
      try {
        await axios.delete(`${API_URL}/api/hr/leave/${id}`);
        toast.success('Request deleted');
        fetchRequests();
      } catch (err) {
        toast.error('Failed to delete');
      }
    }
  };

  const openNew = () => {
    setFormData({ leave_type: 'annual' });
    setShowDialog(true);
  };

  const getEmployeeName = (id) => {
    const emp = employees.find(e => e.id === id);
    return emp ? `${emp.first_name} ${emp.last_name}` : '-';
  };

  const calculateDays = () => {
    if (!formData.start_date || !formData.end_date) return 0;
    return differenceInDays(new Date(formData.end_date), new Date(formData.start_date)) + 1;
  };

  const filteredRequests = requests.filter(r => 
    filterStatus === 'all' || r.status === filterStatus
  );

  const stats = {
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
    totalDays: requests.filter(r => r.status === 'approved').reduce((sum, r) => sum + (r.days || 0), 0)
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Leave Management</h1>
          <p className="text-muted-foreground">Manage employee time-off requests.</p>
        </div>
        <Button onClick={openNew} className="gap-2"><Plus className="h-4 w-4" /> Request Leave</Button>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><Clock className="h-8 w-8 text-amber-500" /><div><div className="text-2xl font-bold">{stats.pending}</div><div className="text-sm text-muted-foreground">Pending</div></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><CheckCircle className="h-8 w-8 text-emerald-500" /><div><div className="text-2xl font-bold">{stats.approved}</div><div className="text-sm text-muted-foreground">Approved</div></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><XCircle className="h-8 w-8 text-red-500" /><div><div className="text-2xl font-bold">{stats.rejected}</div><div className="text-sm text-muted-foreground">Rejected</div></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><CalendarOff className="h-8 w-8 text-blue-500" /><div><div className="text-2xl font-bold">{stats.totalDays}</div><div className="text-sm text-muted-foreground">Days Approved</div></div></div></CardContent></Card>
      </div>

      <div className="flex items-center gap-4">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Filter" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Start Date</TableHead>
              <TableHead>End Date</TableHead>
              <TableHead>Days</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRequests.map((request) => (
              <TableRow key={request.id}>
                <TableCell className="font-medium">{getEmployeeName(request.employee_id)}</TableCell>
                <TableCell>
                  <span className={`flex items-center gap-2 ${leaveTypes[request.leave_type]?.color || ''}`}>
                    {leaveTypes[request.leave_type]?.icon}
                    {leaveTypes[request.leave_type]?.label || request.leave_type}
                  </span>
                </TableCell>
                <TableCell>{format(new Date(request.start_date), 'MMM d, yyyy')}</TableCell>
                <TableCell>{format(new Date(request.end_date), 'MMM d, yyyy')}</TableCell>
                <TableCell className="font-semibold">{request.days}</TableCell>
                <TableCell className="max-w-xs truncate text-muted-foreground">{request.reason || '-'}</TableCell>
                <TableCell><Badge className={statusColors[request.status]}>{request.status}</Badge></TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {request.status === 'pending' && (
                        <>
                          <DropdownMenuItem onClick={() => handleApprove(request.id)}><CheckCircle className="h-4 w-4 mr-2 text-emerald-600" /> Approve</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleReject(request.id)}><XCircle className="h-4 w-4 mr-2 text-red-600" /> Reject</DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      )}
                      <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(request.id)}>
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {filteredRequests.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="h-32 text-center">
                  <CalendarOff className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground">No leave requests found</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Request Leave</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select value={formData.employee_id || ''} onValueChange={v => setFormData({...formData, employee_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Leave Type</Label>
              <Select value={formData.leave_type || 'annual'} onValueChange={v => setFormData({...formData, leave_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(leaveTypes).map(([key, val]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">{val.icon} {val.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input type="date" value={formData.start_date || ''} onChange={e => setFormData({...formData, start_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>End Date *</Label>
                <Input type="date" value={formData.end_date || ''} onChange={e => setFormData({...formData, end_date: e.target.value})} />
              </div>
            </div>
            {formData.start_date && formData.end_date && (
              <div className="bg-muted/50 rounded-lg p-3 text-center">
                <span className="text-2xl font-bold">{calculateDays()}</span>
                <span className="text-muted-foreground ml-2">day(s)</span>
              </div>
            )}
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea value={formData.reason || ''} onChange={e => setFormData({...formData, reason: e.target.value})} rows={3} placeholder="Optional reason for leave..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave}>Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeaveManagement;
